/**
 * Shared formatting utilities for the stock trading app.
 * All pages should use these instead of defining local formatters.
 */

/**
 * Format a number as USD currency string.
 * @param {number} amount
 * @returns {string} e.g. "$1,234.56" or "-$500.00"
 */
export function formatCurrency(amount) {
  if (amount === null || amount === undefined || isNaN(amount)) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Format a number as a percentage string with +/- sign.
 * @param {number} value
 * @returns {string} e.g. "+1.25%" or "-0.50%"
 */
export function formatPercent(value) {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  const num = parseFloat(value);
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
}

/**
 * Format a large number as a compact volume string.
 * @param {number} volume
 * @returns {string} e.g. "1.2M", "450K", "8,500"
 */
export function formatVolume(volume) {
  if (volume === null || volume === undefined || isNaN(volume)) return 'N/A';
  const v = parseFloat(volume);
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
}

/**
 * Get the MUI color token for a P&L value.
 * @param {number} value
 * @returns {string} 'success.main' for >= 0, 'error.main' for < 0
 */
export function getPnLColor(value) {
  return parseFloat(value) >= 0 ? 'success.main' : 'error.main';
}

/**
 * Format a date string to a localized short date.
 * @param {string} dateString - ISO date string or parseable date
 * @returns {string} e.g. "May 23, 2026"
 */
export function formatDate(dateString) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date string to include time.
 * @param {string} dateString
 * @returns {string} e.g. "May 23, 2026, 2:30 PM"
 */
export function formatDateTime(dateString) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
